import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import './DatePicker.scss';

const LT_MONTHS = [
  'Sausis', 'Vasaris', 'Kovas', 'Balandis', 'Gegužė', 'Birželis',
  'Liepa', 'Rugpjūtis', 'Rugsėjis', 'Spalis', 'Lapkritis', 'Gruodis',
];
const LT_MONTHS_SHORT = [
  'Sau', 'Vas', 'Kov', 'Bal', 'Geg', 'Bir',
  'Lie', 'Rugp', 'Rugs', 'Spa', 'Lap', 'Gru',
];
// Week starts Monday in Lithuania
const LT_WEEKDAYS = ['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'];

interface DatePickerProps {
  value: string; // ISO date string yyyy-mm-dd or empty
  onChange: (value: string) => void;
  placeholder?: string;
  minDate?: string; // yyyy-mm-dd
  maxDate?: string; // yyyy-mm-dd
  label?: string;
  className?: string;
  clearable?: boolean;
}

function parseDate(str: string): Date | null {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDisplay(str: string): string {
  const d = parseDate(str);
  if (!d) return '';
  return `${String(d.getDate()).padStart(2, '0')} ${LT_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Pasirinkite datą',
  minDate,
  maxDate,
  className = '',
  clearable = true,
}) => {
  const today = new Date();
  const selected = parseDate(value);

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(selected?.getFullYear() ?? today.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected?.getMonth() ?? today.getMonth());
  const [mode, setMode] = useState<'days' | 'months' | 'years'>('days');

  const containerRef = useRef<HTMLDivElement>(null);
  // Tracks the pixel position of the input so the portal-rendered dropdown
  // can be placed directly below it regardless of any overflow:hidden parents.
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const minD = parseDate(minDate ?? '');
  const maxD = parseDate(maxDate ?? '');

  useEffect(() => {
    if (selected) {
      setViewYear(selected.getFullYear());
      setViewMonth(selected.getMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Recalculate dropdown position whenever it opens or the page scrolls/resizes.
  const DROPDOWN_W = 280;
  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dropdownHeight = 310; // approximate calendar height
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
    // Align left edge with input; shift left if it would overflow the viewport
    const leftPos = Math.min(rect.left, window.innerWidth - DROPDOWN_W - 8);
    setDropdownStyle({
      position: 'fixed',
      top: openUpward ? rect.top - dropdownHeight - 4 : rect.bottom + 6,
      left: Math.max(8, leftPos),
      width: DROPDOWN_W,
      zIndex: 9999,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateDropdownPosition();
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [open, updateDropdownPosition]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      // Close if click is outside both the input wrapper and the portal dropdown
      const dropdownEl = document.querySelector('.date-picker-wrapper.date-picker-dropdown-portal');
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        (!dropdownEl || !dropdownEl.contains(target))
      ) {
        setOpen(false);
        setMode('days');
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const isDisabled = useCallback((d: Date) => {
    if (minD && d < minD) return true;
    if (maxD && d > maxD) return true;
    return false;
  }, [minD, maxD]);

  const selectDate = (d: Date) => {
    if (isDisabled(d)) return;
    onChange(toIso(d));
    setOpen(false);
    setMode('days');
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  // Build calendar grid (Monday-first)
  const buildDays = () => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    // JS: 0=Sun, 1=Mon ... shift to Mon=0
    const startDow = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells: (Date | null)[] = Array(startDow).fill(null);
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push(new Date(viewYear, viewMonth, i));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  };

  const yearRange = () => {
    const base = Math.floor(viewYear / 12) * 12;
    return Array.from({ length: 12 }, (_, i) => base + i);
  };

  const todayStr = toIso(today);

  return (
    <div className={`date-picker-wrapper ${className} ${open ? 'open' : ''}`} ref={containerRef}>
      <div
        className="date-picker-input"
        onClick={() => { setOpen(o => !o); setMode('days'); }}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setOpen(o => !o)}
      >
        <Calendar size={16} className="dp-icon" />
        <span className={value ? 'dp-value' : 'dp-placeholder'}>
          {value ? formatDisplay(value) : placeholder}
        </span>
        {clearable && value && (
          <button
            className="dp-clear"
            onClick={e => { e.stopPropagation(); onChange(''); }}
            title="Išvalyti"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && createPortal(
        <div className="date-picker-wrapper date-picker-dropdown-portal" style={{ position: dropdownStyle.position as any, top: dropdownStyle.top, left: dropdownStyle.left, width: dropdownStyle.width, zIndex: dropdownStyle.zIndex }}>
        <div className="date-picker-dropdown">
          {/* ── Header ── */}
          <div className="dp-header">
            <button className="dp-nav" onClick={mode === 'years' ? () => setViewYear(y => y - 12) : prevMonth}><ChevronLeft size={16} /></button>
            <div className="dp-title-row">
              <button className="dp-title-btn" onClick={() => setMode(m => m === 'months' ? 'days' : 'months')}>
                {LT_MONTHS[viewMonth]}
              </button>
              <button className="dp-title-btn" onClick={() => setMode(m => m === 'years' ? 'days' : 'years')}>
                {viewYear}
              </button>
            </div>
            <button className="dp-nav" onClick={mode === 'years' ? () => setViewYear(y => y + 12) : nextMonth}><ChevronRight size={16} /></button>
          </div>

          {/* ── Days view ── */}
          {mode === 'days' && (
            <>
              <div className="dp-weekdays">
                {LT_WEEKDAYS.map(d => <span key={d}>{d}</span>)}
              </div>
              <div className="dp-grid">
                {buildDays().map((d, i) => {
                  if (!d) return <span key={i} className="dp-cell empty" />;
                  const iso = toIso(d);
                  const isToday = iso === todayStr;
                  const isSel = iso === value;
                  const dis = isDisabled(d);
                  return (
                    <button
                      key={i}
                      className={`dp-cell${isToday ? ' today' : ''}${isSel ? ' selected' : ''}${dis ? ' disabled' : ''}`}
                      onClick={() => selectDate(d)}
                      disabled={dis}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Months view ── */}
          {mode === 'months' && (
            <div className="dp-months-grid">
              {LT_MONTHS.map((m, i) => (
                <button
                  key={i}
                  className={`dp-month-cell${viewMonth === i ? ' selected' : ''}`}
                  onClick={() => { setViewMonth(i); setMode('days'); }}
                >
                  {m.slice(0, 3)}
                </button>
              ))}
            </div>
          )}

          {/* ── Years view ── */}
          {mode === 'years' && (
            <div className="dp-years-grid">
              {yearRange().map(y => (
                <button
                  key={y}
                  className={`dp-year-cell${viewYear === y ? ' selected' : ''}`}
                  onClick={() => { setViewYear(y); setMode('days'); }}
                >
                  {y}
                </button>
              ))}
            </div>
          )}

          {/* ── Footer ── */}
          <div className="dp-footer">
            <button className="dp-today-btn" onClick={() => { onChange(todayStr); setOpen(false); setMode('days'); }}>
              Šiandien
            </button>
            {clearable && value && (
              <button className="dp-clear-btn" onClick={() => { onChange(''); setOpen(false); }}>
                Išvalyti
              </button>
            )}
          </div>
        </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DatePicker;
